<script setup>
import { storeToRefs } from 'pinia';
import { ref, watch } from 'vue';

import { fetchCsrfBootstrap } from '@/api/auth.js';
import { api } from '@/api/client.js';
import Button from '@/components/ds/Button.vue';
import PanelCard from '@/components/panels/PanelCard.vue';
import { usePanelState } from '@/composables/usePanelState.js';
import { usePoll } from '@/composables/usePoll.js';
import { useAuthStore } from '@/stores/auth.js';
import { useTenantStore } from '@/stores/tenant.js';

const auth = useAuthStore();
const tenant = useTenantStore();
const { activeAppId } = storeToRefs(tenant);
const ps = usePanelState('loading');
const candidates = ref([]);
const severity = ref({});
const busy = ref(null);
const actionError = ref('');
const selected = ref(null);
const deliveries = ref([]);
const deliveryState = ref('idle');
watch(() => tenant.changeKey, () => {
  candidates.value=[];selected.value=null;deliveries.value=[];deliveryState.value='idle';
  actionError.value='';ps.setState('loading');
});

const { tick, status: refreshStatus, lastUpdatedAt } = usePoll(async ({signal}) => {
  if(!activeAppId.value)return [];
  const response=await api.events.incidentCandidates({appId:activeAppId.value,signal});
  if(!response?.ok)throw new Error('Invalid incident response');
  return response.candidates??[];
}, {watch:[()=>tenant.changeKey],onSuccess(rows){
  candidates.value=rows;
  for(const row of rows) if(!severity.value[row.candidate_id])
    severity.value[row.candidate_id]=row.confirmed_severity??row.proposed_severity;
  ps.setState(rows.length?'data':'empty');
},onError(error){ps.setState('error',{message:'Could not load incident candidates',detail:error?.message??''});}});
ps.setRetryHandler(()=>tick());
defineExpose({ps,refreshStatus});

async function mutate(candidate,action) {
  if(!auth.isAdmin||busy.value)return;
  busy.value=candidate.candidate_id;actionError.value='';
  try {
    const csrf=(await fetchCsrfBootstrap()).csrfToken;
    if(action==='confirm')await api.admin.confirmIncident({candidateId:candidate.candidate_id,
      severity:severity.value[candidate.candidate_id],csrf});
    else await api.admin.discardIncident({candidateId:candidate.candidate_id,csrf});
    await tick();
  } catch(error) {actionError.value=error?.message??'Action failed';}
  finally {busy.value=null;}
}

async function openDeliveries(candidate) {
  selected.value=selected.value===candidate.candidate_id?null:candidate.candidate_id;
  if(!selected.value)return;
  const context=tenant.changeKey;
  deliveryState.value='loading';
  try {
    const response=await api.events.incidentDeliveries({appId:activeAppId.value,
      candidateId:candidate.candidate_id});
    if(context!==tenant.changeKey)return;
    deliveries.value=response.deliveries??[];deliveryState.value='data';
  } catch {if(context===tenant.changeKey)deliveryState.value='error';}
}
const date = value => value ? new Date(value).toLocaleString() : '—';
</script>

<template>
  <PanelCard
    title="Incident candidates"
    :state="ps.state.value"
    empty-headline="No incident candidates"
    empty-body="Signals have not met an enabled incident rule."
    :error-message="ps.errorMessage.value"
    :error-detail="ps.errorDetail.value"
    :on-retry="ps.retry"
    :refresh-status="refreshStatus"
    :last-updated-at="lastUpdatedAt"
  >
    <p class="incident-hint">
      Candidates require a person to confirm severity before any alert is queued. Counts reflect distinct accepted facts.
    </p>
    <p
      v-if="actionError"
      role="alert"
    >
      {{ actionError }}
    </p>
    <ul class="incident-list">
      <li
        v-for="candidate in candidates"
        :key="candidate.candidate_id"
        class="incident-row"
      >
        <div><strong>{{ candidate.signal }}</strong> · {{ candidate.module_key }} / {{ candidate.field_key }}</div>
        <div class="incident-hint">
          {{ candidate.state }}<span v-if="candidate.resolved_at"> · signal recovered {{ date(candidate.resolved_at) }}</span> · {{ candidate.occurrences }} facts · first {{ date(candidate.first_seen_at) }} · latest {{ date(candidate.last_seen_at) }}
        </div>
        <div
          v-if="auth.isAdmin && candidate.state==='candidate'"
          class="incident-actions"
        >
          <label>Proposed severity
            <select v-model="severity[candidate.candidate_id]">
              <option
                v-for="level in ['S1','S2','S3','S4']"
                :key="level"
              >{{ level }}</option>
            </select>
          </label>
          <Button
            size="sm"
            :loading="busy===candidate.candidate_id"
            @click="mutate(candidate,'confirm')"
          >
            Confirm
          </Button>
          <Button
            size="sm"
            variant="secondary"
            :disabled="!!busy"
            @click="mutate(candidate,'discard')"
          >
            Discard
          </Button>
        </div>
        <Button
          size="sm"
          variant="secondary"
          :aria-expanded="selected===candidate.candidate_id"
          @click="openDeliveries(candidate)"
        >
          Delivery log
        </Button>
        <div
          v-if="selected===candidate.candidate_id"
          role="status"
        >
          <p v-if="deliveryState==='loading'">
            Loading delivery log…
          </p>
          <p v-else-if="deliveryState==='error'">
            Could not load delivery log.
          </p>
          <p v-else-if="!deliveries.length">
            No delivery has been queued.
          </p>
          <ul v-else>
            <li
              v-for="delivery in deliveries"
              :key="delivery.job_id"
            >
              {{ delivery.channel }} · {{ delivery.state }} · {{ delivery.attempts }} attempts · {{ date(delivery.sent_at) }}
              <span v-if="delivery.last_error_code"> · {{ delivery.last_error_code }}</span>
              <a
                v-if="delivery.ticket_url"
                :href="delivery.ticket_url"
                target="_blank"
                rel="noopener noreferrer"
              >Ticket</a>
            </li>
          </ul>
        </div>
      </li>
    </ul>
  </PanelCard>
</template>

<style scoped>
.incident-hint {
  color: var(--fg-secondary);
  font-size: var(--text-xs);
}

.incident-list {
  display: grid;
  gap: var(--space-3);
  padding: 0;
  list-style: none;
}

.incident-row {
  padding: var(--space-3);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-md);
}

.incident-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  align-items: end;
  margin: var(--space-2) 0;
}

.incident-actions label {
  display: grid;
  gap: var(--space-1);
}
</style>
